/**
 * Preços exibidos na vitrine — os DOIS motores, uma forma só.
 *
 * ── A regra que este arquivo existe para cumprir ────────────────────────────
 *
 * Na web quem cobra é o Stripe e o preço anunciado vem de `constants/pricing.ts`
 * (a fonte única criada depois do defeito de 02/08, em que a `/pro` anunciava
 * R$ 9,90 e o cartão era debitado em R$ 14,99).
 *
 * No nativo quem cobra é a LOJA, e o preço é o que a loja diz — sempre. Não é
 * preferência de estilo: a App Store e o Play cobram na moeda e no imposto da
 * conta do usuário, aplicam preço regional e podem estar num tier diferente do
 * que combinamos. Repetir `R$ 14,90` de `pricing.ts` numa tela nativa
 * reproduziria o defeito de 02/08 em escala — anunciando um número e cobrando
 * outro, agora para cada país. Por isso a vitrine nativa lê `priceString` do
 * `PurchasesStoreProduct`, que já vem formatado e localizado pelo SDK.
 *
 * ⚠️ **Nada aqui formata dinheiro à mão.** `priceString`, `pricePerMonthString`
 * e afins vêm prontos do SDK. Um `R$ ${n.toFixed(2)}` nesta camada quebraria
 * toda conta fora do Brasil.
 */

import type { PurchasesOffering, PurchasesPackage } from "../../../lib/purchases/revenueCat";
import {
  PRO_PRICE,
  PRO_TRIAL_DAYS,
  founderPriceFor,
} from "../constants/pricing";
import type { IapProduct } from "./iapProducts";

/**
 * O que a tela precisa saber para desenhar preço. `null` significa "não há o
 * que anunciar" — produto desligado por flag, ausente do offering, ou uma
 * grandeza que não dá para calcular. Nunca significa zero.
 */
export interface ProPrices {
  monthly: string | null;
  annual: string | null;
  /** Equivalente mensal do anual. É CONTA, não preço cobrado. */
  annualPerMonth: string | null;
  /** Ex.: `"33%"`. `null` quando não dá para comparar com o mensal. */
  annualSavings: string | null;
  founder: string | null;
  /**
   * Dias de teste grátis do MENSAL **disponíveis a este usuário**. `null` =
   * não anunciar.
   *
   * A diferença entre `null` e um número é a diferença entre "a loja não
   * garante" e "a loja garante" — e o texto "7 dias grátis" só pode existir no
   * segundo caso. Ver `trialDaysFromProduct`.
   */
  trialDays: number | null;
  /**
   * O mesmo para o ANUAL (ganhou trial em 16/09). Campo separado porque no
   * nativo a elegibilidade é POR PRODUTO — a loja pode confirmar um e negar o
   * outro (ou uma loja ter a oferta configurada e a outra ainda não). Na web
   * os dois andam juntos. Leia pelo plano com `trialDaysFor`.
   */
  annualTrialDays: number | null;
}

/** Trial do plano selecionado — `null` quando não há o que prometer. */
export function trialDaysFor(
  prices: Pick<ProPrices, "trialDays" | "annualTrialDays">,
  plan: "monthly" | "annual",
): number | null {
  return plan === "annual" ? prices.annualTrialDays : prices.trialDays;
}

/**
 * O número a anunciar na ABERTURA da vitrine (`ProTrialInvite`), antes de a
 * pessoa escolher plano: o mensal quando existe, senão o anual. `null` = os
 * dois calados = não convidar.
 */
export function trialInviteDays(
  prices: Pick<ProPrices, "trialDays" | "annualTrialDays">,
): number | null {
  return prices.trialDays ?? prices.annualTrialDays;
}

/**
 * Preços da web: os anunciados, já decididos pelo fundador — ou o override
 * do painel (Bloco H, `docs/systems/admin-pro-showcase.md`), quando existe.
 *
 * `priceOverride`/`trialDaysOverride` vêm de `useProShowcaseOverlay()`; o
 * chamador decide se lê o overlay (a vitrine, que PODE mudar sem deploy) ou
 * não (o Founder — `founder` abaixo NUNCA lê o overlay, fora de escopo do
 * Bloco H por desenho, ver blueprint §5.1).
 *
 * `trialConsumed` (`users.proTrialConsumedAt` presente) cala o trial nos dois
 * planos: o `createStripeCheckoutSession` vai recusá-lo, e um botão dizendo
 * "Começar 7 dias grátis" seguido de cobrança imediata é o pior dos mundos.
 */
export function webPrices(
  founderSold: number,
  priceOverride?: {
    monthly: string;
    annual: string;
    annualPerMonth: string;
    annualSavings: string;
  } | null,
  trialDaysOverride?: number | null,
  trialConsumed = false,
): ProPrices {
  const trial = trialConsumed ? null : (trialDaysOverride ?? PRO_TRIAL_DAYS);
  return {
    monthly: priceOverride?.monthly ?? PRO_PRICE.monthly,
    annual: priceOverride?.annual ?? PRO_PRICE.annual,
    annualPerMonth: priceOverride?.annualPerMonth ?? PRO_PRICE.annualPerMonth,
    annualSavings: priceOverride?.annualSavings ?? PRO_PRICE.annualSavings,
    founder: founderPriceFor(founderSold),
    trialDays: trial,
    annualTrialDays: trial,
  };
}

/** Os três packages do offering, resolvidos. */
export interface NativePackages {
  monthly: PurchasesPackage | null;
  annual: PurchasesPackage | null;
  founder: PurchasesPackage | null;
}

/**
 * Identificadores aceitos para o package do Founder.
 *
 * Founder é **non-consumable**, então ele não cai em `offering.monthly`
 * /`.annual`; entra como package customizado. O `lifetime` do RC é tentado
 * primeiro (é o tipo predefinido semanticamente correto), e depois a busca por
 * identificador na lista — que é como o offering `default` foi configurado.
 *
 * ⚠️ A busca é por IDENTIFICADOR DE PACKAGE, nunca por product id. Os product
 * ids divergem entre as lojas por construção (`livra_pro_mensal` na Apple,
 * `livra_pro:mensal` no Play, porque o Play usa `subId:basePlanId`), e uma
 * vitrine que casasse SKU precisaria de uma tabela por plataforma — que é
 * exatamente a tabela que envelhece sem ninguém notar.
 */
const FOUNDER_PACKAGE_IDS: readonly string[] = ["founder", "$rc_lifetime", "lifetime"];

/** Resolve os packages, respeitando os produtos ligados por flag. */
export function packagesFromOffering(
  offering: PurchasesOffering | null,
  enabled: ReadonlySet<IapProduct>,
): NativePackages {
  if (!offering) return { monthly: null, annual: null, founder: null };

  const founder =
    offering.lifetime ??
    offering.availablePackages.find((p) =>
      FOUNDER_PACKAGE_IDS.includes(p.identifier.toLowerCase()),
    ) ??
    null;

  return {
    monthly: enabled.has("monthly") ? offering.monthly : null,
    annual: enabled.has("annual") ? offering.annual : null,
    founder: enabled.has("founder") ? founder : null,
  };
}

/** Dias equivalentes de um período de oferta introdutória. */
function periodToDays(unit: string, units: number): number | null {
  switch (unit.toUpperCase()) {
    case "DAY":
      return units;
    case "WEEK":
      return units * 7;
    case "MONTH":
      return units * 30;
    case "YEAR":
      return units * 365;
    default:
      return null;
  }
}

/**
 * Duração do teste GRÁTIS do produto, ou `null`.
 *
 * Exige `price === 0`: uma oferta introdutória **paga** (preço reduzido nos
 * primeiros meses) também vive em `introPrice`, e anunciá-la como "grátis"
 * seria falso. A distinção é do SDK, não nossa — só não estava sendo lida.
 */
export function trialDaysFromProduct(pkg: PurchasesPackage | null): number | null {
  const intro = pkg?.product.introPrice;
  if (!intro || intro.price !== 0) return null;
  return periodToDays(intro.periodUnit, intro.periodNumberOfUnits);
}

/**
 * Duração do teste grátis que a PLAY oferece A ESTA CONTA, ou `null` (23/09).
 *
 * No Android `checkTrialOrIntroductoryPriceEligibility` responde `UNKNOWN`
 * sempre — e até aqui isso calava o convite de teste para todo Android, embora
 * a pessoa pudesse fazê-lo (houve teste pela Play em 07/09). A resposta estava
 * no próprio produto:
 *   · a Play só devolve, em `queryProductDetails`, as ofertas para as quais a
 *     conta é ELEGÍVEL (critério "novo cliente"/"upgrade"), e a RevenueCat as
 *     repassa sem filtrar (`subscriptionOptions`);
 *   · `defaultOption` é, entre elas, a do teste grátis mais longo — e é ele que
 *     `purchasePackage` compra.
 * Então `defaultOption.freePhase` presente = a Play vai dar o teste, e o que a
 * tela anuncia é exatamente o que a folha da loja cobra.
 *
 * ⚠️ Pressupõe a oferta `trial7d` com critério de elegibilidade da Play, NÃO
 * "determinado pelo desenvolvedor" — esse tipo aparece sempre, elegível ou não
 * (`docs/systems/revenuecat-billing.md` §5). No iOS `defaultOption` é `null`
 * por definição, então nada muda lá.
 */
export function playTrialDaysFromProduct(pkg: PurchasesPackage | null): number | null {
  const free = pkg?.product.defaultOption?.freePhase;
  if (!free) return null;
  const days = periodToDays(free.billingPeriod.unit, free.billingPeriod.value);
  if (days == null) return null;
  // `billingCycleCount` ausente = uma fase só (teste de 7 dias é 1 ciclo de P1W).
  return days * (free.billingCycleCount ?? 1);
}

/**
 * Dias de teste a anunciar para UM produto, dada a resposta da loja.
 * `true` → a oferta introdutória do produto; `false` → nunca (a loja disse
 * não); `null` (UNKNOWN) → só o que a Play garante pelo `defaultOption`.
 */
function trialDaysForEligibility(
  pkg: PurchasesPackage | null,
  eligible: boolean | null,
): number | null {
  if (eligible === true) return trialDaysFromProduct(pkg);
  if (eligible === null) return playTrialDaysFromProduct(pkg);
  return null;
}

/**
 * Economia do anual contra 12× o mensal, arredondada. `null` quando falta um
 * dos dois preços ou quando o anual não é mais barato — anunciar "−0%" ou uma
 * economia negativa seria pior que omitir.
 */
export function annualSavingsFrom(pkgs: NativePackages): string | null {
  const m = pkgs.monthly?.product.price;
  const a = pkgs.annual?.product.price;
  if (!m || !a || m <= 0) return null;
  const pct = Math.round((1 - a / (m * 12)) * 100);
  return pct > 0 ? `${pct}%` : null;
}

/** Elegibilidade de trial POR PRODUTO, como a loja responde. */
export interface TrialEligibility {
  monthly: boolean | null;
  annual: boolean | null;
}

/**
 * Preços da vitrine nativa, direto da loja.
 *
 * `trialEligible` é a resposta de `checkTrialOrIntroductoryPriceEligibility`,
 * por produto: `true` libera o anúncio e `false` o suprime. `null` (UNKNOWN —
 * o Android sempre) NÃO é "sim": só anuncia se a própria Play entregou a oferta
 * grátis no `defaultOption` do produto (`playTrialDaysFromProduct`), porque ela
 * só entrega o que a conta pode usar. Sem isso, desconhecido continua calado —
 * prometer trial sob desconhecido é prometer o que a loja pode não dar. Cada plano só anuncia o
 * trial do PRÓPRIO produto: a oferta do anual nasceu depois da do mensal nas
 * duas lojas (16/09), e um build que copiasse a resposta do mensal para o anual
 * teria prometido teste num produto que ainda não o tinha.
 */
export function nativePrices(
  pkgs: NativePackages,
  trialEligible: TrialEligibility,
): ProPrices {
  return {
    monthly: pkgs.monthly?.product.priceString ?? null,
    annual: pkgs.annual?.product.priceString ?? null,
    // Vem do SDK já dividido por 12 e formatado na moeda da conta.
    annualPerMonth: pkgs.annual?.product.pricePerMonthString ?? null,
    annualSavings: annualSavingsFrom(pkgs),
    founder: pkgs.founder?.product.priceString ?? null,
    trialDays: trialDaysForEligibility(pkgs.monthly, trialEligible.monthly),
    annualTrialDays: trialDaysForEligibility(pkgs.annual, trialEligible.annual),
  };
}
